/**
 * Authentication service
 * Supports Azure AD authentication only - mock Azure AD is completely removed
 */
import { BaseService } from '@services/base.service';
import { LoginRequest, RegisterRequest, AuthResponse } from '../types/auth.types';
import { getAuthConfig, AuthProvider } from '@core/config/auth.config';
import { azureADService } from './azure-ad.service';
import { mockAuthService } from './mock-auth.service';
import { getFastAPIBaseUrl } from '@services/proxy.service';
import axios from 'axios';
import { API_TIMEOUT } from '@core/constants/app.constants';

/**
 * Authentication service with Azure AD SSO integration
 * 
 * This service implements the complete Azure AD SSO flow:
 * 1. Microsoft OAuth (via MSAL) → Get id_token
 * 2. Backend SSO endpoint → Exchange id_token for access_token
 * 3. API calls → Use access_token as Bearer token
 * 
 * Mock Azure AD is completely removed - always use real Azure AD
 */

class AuthService extends BaseService<any> {
    constructor() {
        super('/auth');
    }

    /**
     * Get Azure AD service - always returns real Azure AD service (no mock)
     */
    private getAzureADService() {
        return azureADService;
    }

    /**
     * Check if we should use mock auth
     */
    private shouldUseMock(): boolean {
        return import.meta.env.DEV && window._env_.AIPORTAL_USE_MOCK_AUTH === 'true';
    }

    /**
     * Initialize authentication
     */
    async initialize(): Promise<void> {
        const authConfig = getAuthConfig();
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            await azureService.initialize();
            console.log('✅ Real Azure AD initialized');
        } else if (this.shouldUseMock()) {
            console.log('✅ Mock auth initialized (Development mode)');
        } else {
            console.log('✅ Local auth initialized');
        }
    }

    /**
     * Login - supports Azure AD (real only), Mock, and Real API
     */
    async login(credentials: LoginRequest): Promise<AuthResponse> {
        const authConfig = getAuthConfig();
        // Azure AD Login - always uses real Azure AD (no mock)
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            return this.loginWithAzureADSSO();
        }

        // Mock Login (Development)
        if (this.shouldUseMock()) {
            return mockAuthService.login(credentials);
        }

        // Real API Login - use FastAPI base URL (same pattern as proxy service)
        const fastApiBaseUrl = getFastAPIBaseUrl();
        const response = await axios.post(`${fastApiBaseUrl}/api/v1${this.endpoint}/login`, credentials, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            withCredentials: true,
            timeout: API_TIMEOUT,
        });
        return response.data.data || response.data;
    }

    /**
     * Azure AD SSO Login - Complete Authentication Flow
     * 
     * This implements the three-step Azure AD SSO authentication flow:
     * 
     * Step 1: Microsoft OAuth Token Endpoint
     * - Authenticate user with Azure AD using MSAL (Microsoft Authentication Library)
     * - MSAL handles the OAuth flow internally and returns an id_token
     * 
     * Step 2: Backend SSO Endpoint
     * - Exchange Microsoft id_token for application-specific access_token and refresh_token
     * - Endpoint: POST /api/v1/auth/sso/azure-ad
     * - Request body: { id_token: "..." }
     * - Response: { user, access_token, refresh_token, token_type: "bearer" }
     * 
     * Step 3: API Authentication
     * - Store tokens in auth store (which persists to localStorage)
     * - All subsequent API calls will use access_token as Bearer token
     * - Axios interceptors automatically add: Authorization: Bearer {access_token}
     * 
     * @returns AuthResponse containing user, accessToken, and refreshToken
     * @throws Error if any step fails
     */
    async loginWithAzureADSSO(): Promise<AuthResponse> {
        const azureService = this.getAzureADService();
        await azureService.initialize();

        // Step 1: Check if user already has Azure AD session - try silent login first
        const account = azureService.getAccount();
        if (account) {
            try {
                const idToken = await azureService.getIdTokenAsync();
                if (idToken) {
                    // Exchange ID token for application tokens
                    const fastApiBaseUrl = getFastAPIBaseUrl();
                    const response = await axios.post(
                        `${fastApiBaseUrl}/api/v1/auth/sso/azure-ad`,
                        { id_token: idToken },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                            },
                            withCredentials: true,
                            timeout: API_TIMEOUT,
                        }
                    );
                    return await this.processSSOResponse(response);
                }
            } catch (error) {
                // Silent login failed - will redirect to Azure AD below
            }
        }

        // Step 2: No existing session or silent login failed - redirect to Azure AD
        await azureService.loginWithRedirect();
        throw new Error('Redirecting to Azure AD...');
    }

    /**
     * Complete Azure AD SSO login after redirect
     * Returns null if there's no redirect response or user is not authenticated (e.g., after logout)
     */
    async completeAzureADSSOAfterRedirect(): Promise<AuthResponse | null> {
        const azureService = this.getAzureADService();

        await azureService.initialize();
        
        // Get redirect response (already handled during initialization)
        const redirectResponse = azureService.getRedirectResponse();
        if (!redirectResponse) {
            return null; // No redirect - user just navigated to login page
        }

        // Check if user is actually authenticated (not a logout redirect)
        if (!azureService.isAuthenticated()) {
            return null; // No account - likely a logout redirect
        }

        // Step 2: Get ID token from Azure AD response
        const idToken = await azureService.getIdTokenAsync();
        if (!idToken) {
            throw new Error('Failed to obtain ID token from Azure AD');
        }

        // Step 3a: Fetch department from Microsoft Graph before creating custom token
        let department = '';
        try {
            const accessToken = await azureService.getGraphAccessToken();
            const graphResponse = await axios.get(
                'https://graph.microsoft.com/v1.0/me?$select=department',
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                    timeout: API_TIMEOUT,
                }
            );
            department = graphResponse.data?.department ?? '';
            console.log('[Azure AD] Graph department fetched:', department);
        } catch (err) {
            console.warn('[Azure AD] Failed to fetch department from Graph API, defaulting to empty string.', err);
        }

        // Step 3b: Exchange ID token for application tokens via backend SSO endpoint
        // POST /api/v1/auth/sso/azure-ad
        // Backend validates the Azure AD token and returns application-specific tokens
        //
        // Use FastAPI base URL (same as proxy service) for consistency
        // Note: No auth token needed here - this is the endpoint that provides the token
        const fastApiBaseUrl = getFastAPIBaseUrl();
        const response = await axios.post(
            `${fastApiBaseUrl}/api/v1/auth/sso/azure-ad`,
            { id_token: idToken, department },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                withCredentials: true, // Include cookies for Azure AD auth
                timeout: API_TIMEOUT,
            }
        );
        
        return await this.processSSOResponse(response);
    }

    /**
     * Process SSO response and extract user information
     * @private
     */
    private async processSSOResponse(response: any): Promise<AuthResponse> {
        // Parse response - backend returns: { userId, access_token, refresh_token, token_type }
        const data = response.data as any;
        if (!response?.data || !data.access_token) {
            console.error('❌ Invalid SSO response structure. Full response:', {
                responseData: response.data,
                responseStatus: response.status,
                responseHeaders: response.headers,
            });
            throw new Error('Invalid SSO response structure: tokens are missing');
        }
        
        // Extract tokens (handle both camelCase and snake_case)
        const accessToken = data.accessToken || data.access_token;
        const refreshToken = data.refreshToken || data.refresh_token || '';
        const userId = data.userId || data.user_id;
        
        // Step 4: Get user information using the access token
        // Decode JWT token to extract user info, or call user endpoint
        let userInfo: any = null;
        
        try {
            // Try to decode JWT token to get basic user info
            const tokenParts = accessToken.split('.');
            if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]));
                userInfo = {
                    id: payload.sub || userId || '',
                    email: payload.email || '',
                    name: payload.email?.split('@')[0] || 'User',
                    roles: payload.roles || [],
                };
            }
        } catch (error) {
            console.warn('Failed to decode JWT token, will fetch user info from API');
        }
        
        // If we couldn't decode token, try to fetch user info from API with retry logic
        if (!userInfo && accessToken) {
            userInfo = await this.fetchUserInfoWithRetry(accessToken, userId);
        }
        
        // Normalize response format
        const authData: AuthResponse = {
            user: userInfo || {
                id: userId || '',
                email: '',
                name: 'User',
                roles: [],
            },
            accessToken: accessToken,
            refreshToken: refreshToken,
        };
        
        // Step 5: Tokens are stored in auth store (which persists to localStorage)
        // Axios interceptors will automatically add Bearer token to all subsequent requests
        return authData;
    }
    
    /**
     * Fetch user info from API
     * @private
     */
    private async fetchUserInfoWithRetry(accessToken: string, userId?: string): Promise<any> {
        const fastApiBaseUrl = getFastAPIBaseUrl();
        
        try {
            const userResponse = await axios.get(`${fastApiBaseUrl}/api/v1/users/me`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                withCredentials: true,
                timeout: API_TIMEOUT,
            });
            
            if (userResponse.data) {
                const userData = userResponse.data.data || userResponse.data;
                return {
                    id: userData.id || userData.uid || userId || '',
                    email: userData.email || '',
                    name: userData.name || (userData.first_name && userData.last_name ? `${userData.first_name} ${userData.last_name}` : userData.email?.split('@')[0] || 'User'),
                    roles: userData.roles || (userData.role ? [userData.role] : []),
                };
            }
        } catch (error) {
            console.warn('Failed to fetch user info from API, using token data');
        }
        
        // Fallback if API call fails
        return {
            id: userId || '',
            email: '',
            name: 'User',
            roles: [],
        };
    }

    /**
     * Register - only for local/mock auth
     */
    async register(data: RegisterRequest): Promise<AuthResponse> {
        const authConfig = getAuthConfig();
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            throw new Error('Registration not supported with Azure AD');
        }

        // Mock Register (Development)
        if (this.shouldUseMock()) {
            return mockAuthService.register(data);
        }

        // Real API Register - use FastAPI base URL (same pattern as proxy service)
        const fastApiBaseUrl = getFastAPIBaseUrl();
        const response = await axios.post(`${fastApiBaseUrl}/api/v1${this.endpoint}/register`, data, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            withCredentials: true,
            timeout: API_TIMEOUT,
        });
        return response.data.data || response.data;
    }

    /**
     * Logout
     * Clears MSAL session and frontend authentication state
     * For Azure AD, uses redirect (no popup)
     */
    async logout(): Promise<void> {
        const authConfig = getAuthConfig();
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            // Clear auth store immediately before redirect
            const { useAuthStore } = await import('@store/auth.store');
            useAuthStore.getState().clearAuth();
            
            // Set flag for success toast on login page
            sessionStorage.setItem('logoutSuccess', 'true');
            
            const azureService = this.getAzureADService();
            // This will redirect to Azure AD logout, then to /login
            await azureService.logout();
            return;
        }

        if (this.shouldUseMock()) {
            await mockAuthService.logout();
            return;
        }

        // Real API Logout - will use FastAPI backend when integrated
        // For now, just clear local state
        // await axiosInstance.post(`${this.endpoint}/logout`);
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const authConfig = getAuthConfig();
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            return azureService.isAuthenticated();
        }
        return false;
    }

    /**
     * Get current user
     */
    async getCurrentUser(): Promise<any> {
        const authConfig = getAuthConfig();
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            return azureService.getUserInfo();
        }

        if (this.shouldUseMock()) {
            return mockAuthService.getCurrentUser();
        }

        // Fallback to API call (for non-Azure AD auth) - use FastAPI base URL
        const fastApiBaseUrl = getFastAPIBaseUrl();
        const response = await axios.get(`${fastApiBaseUrl}/api/v1${this.endpoint}/me`, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            withCredentials: true,
            timeout: API_TIMEOUT,
        });
        return response.data.data || response.data;
    }
}

export const authService = new AuthService();
